import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, message, Modal, Tag, Tabs, Spin, QRCode } from 'antd';
import { ArrowLeftOutlined, QrcodeOutlined } from '@ant-design/icons';
import TableFetchData from '../../components/data-table/TableFetchData';
import HeaderComponent from '../../components/header/headerComponent';
import SearchSvg from '../../assets/images/SearchSvg';
import { ExportExcelSvg } from '../../assets/images/HeaderSvg';
import * as XLSX from 'xlsx-js-style';
import {
  EVENTS_URL,
  EVENT_SUBMISSIONS_URL,
  EVENT_ADMISSION_URL,
} from '../../api/URLs';
import { getDataRequest } from '../../api/serviceMethods';
import { formatDateTime } from '../../utils/dateFormatUtils';
import '../../components/dashboard/tabs.css';
import '../../styles/event.css';
import logo from '../../assets/images/logo1.svg';

const DEFAULT_POSTER =
  'https://fastly.picsum.photos/id/30/1280/901.jpg?hmac=A_hpFyEavMBB7Dsmmp53kPXKmatwM05MUDatlWSgATE';

const formatDatePart = (isoStr) => {
  if (!isoStr) return '';
  try {
    const dt = new Date(isoStr);
    if (isNaN(dt.getTime())) return isoStr;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}.${m}.${d}`;
  } catch {
    return isoStr;
  }
};

export const normalizeSubmission = (item, currentLang = 'cn') => {
  if (!item) return null;

  const locObj =
    item.localize ||
    item.submission?.localize ||
    item.submissionsId?.localize ||
    item.submissionId?.localize ||
    item.eventsId?.localize ||
    {};

  let layout = [];
  if (Array.isArray(item.layout)) {
    layout = item.layout;
  } else if (Array.isArray(item.submission?.layout)) {
    layout = item.submission.layout;
  }

  const candidateBlocks = [
    locObj['cn'],
    locObj[currentLang],
    locObj['en'],
    ...Object.values(locObj),
  ].filter(Boolean);

  if (layout.length === 0) {
    for (const block of candidateBlocks) {
      if (
        Array.isArray(block.layout) &&
        block.layout.some((q) =>
          Array.isArray(q.answers) ? q.answers.length > 0 : q.answers !== null && q.answers !== undefined && q.answers !== ''
        )
      ) {
        layout = block.layout;
        break;
      }
    }
  }

  if (layout.length === 0) {
    const fallbackBlock =
      locObj[currentLang] || locObj['cn'] || locObj['en'] || Object.values(locObj)[0] || {};
    layout = Array.isArray(fallbackBlock.layout) ? fallbackBlock.layout : [];
  }

  const getAnswerStr = (answers) => {
    if (Array.isArray(answers)) {
      return answers
        .filter((a) => a !== null && a !== undefined && a !== '')
        .map((a) => String(a).trim())
        .filter(Boolean)
        .join(', ');
    }
    if (answers !== null && answers !== undefined && answers !== '') {
      return String(answers).trim();
    }
    return '';
  };

  const extractPhone = (q) => {
    const answerStr = getAnswerStr(q.answers);
    if (!answerStr) return '';

    let selectedCc = '';
    if (Array.isArray(q.countryCodes)) {
      const answeredItem = q.countryCodes.find(
        (c) =>
          c &&
          (c.isanswer === true ||
            c.isanswer === 'true' ||
            String(c.isanswer).toLowerCase() === 'true')
      );
      if (answeredItem) {
        selectedCc = String(answeredItem.value || answeredItem.label || '').trim();
      }
    }
    if (!selectedCc && q.defaultCountryCode) {
      selectedCc = String(q.defaultCountryCode).trim();
    }
    if (selectedCc && !selectedCc.startsWith('+') && /^\d+$/.test(selectedCc)) {
      selectedCc = `+${selectedCc}`;
    }

    if (answerStr.startsWith('+')) {
      return answerStr;
    }
    if (selectedCc) {
      if (answerStr.startsWith(selectedCc)) {
        return answerStr;
      }
      return `${selectedCc} ${answerStr}`.trim();
    }
    return answerStr;
  };

  let name = '';
  let phone = '';
  let company = '';
  let email = '';
  let code = '';

  const processLayout = (lay) => {
    if (!Array.isArray(lay)) return;
    lay.forEach((q) => {
      const title = String(q.title || '').trim().toLowerCase();
      const qType = String(q.questionType || '').toLowerCase();
      const qCode = String(q.code || '').trim().toUpperCase();
      const answerStr = getAnswerStr(q.answers);

      // 1. Phone Question (check isanswer for countryCode)
      if (
        !phone &&
        (qType === 'phone' ||
          title.includes('手机') ||
          title.includes('电话') ||
          title.includes('phone') ||
          title.includes('mobile'))
      ) {
        const extracted = extractPhone(q);
        if (extracted) phone = extracted;
      }

      // 2. Email Question (get email from answers)
      if (
        !email &&
        (qType === 'email' ||
          title.includes('电邮') ||
          title.includes('邮箱') ||
          title.includes('email'))
      ) {
        if (answerStr) email = answerStr;
      }

      // 3. Name Question
      if (
        !name &&
        (title.includes('姓名') ||
          title.includes('name') ||
          (qType === 'text' && title === '姓名'))
      ) {
        if (answerStr) name = answerStr;
      }

      // 4. Company / Salon Question
      if (
        !company &&
        (title.includes('发廊') ||
          title.includes('公司') ||
          title.includes('salon') ||
          title.includes('company'))
      ) {
        if (answerStr) company = answerStr;
      }

      // 5. Invitation Code Question
      if (
        !code &&
        (qCode === 'INVITATION-CODE' ||
          title.includes('邀请码') ||
          title.includes('invite') ||
          title.includes('code'))
      ) {
        if (answerStr) code = answerStr;
      }
    });
  };

  processLayout(layout);

  if (!phone || !email || !name || !company || !code) {
    for (const block of candidateBlocks) {
      if (Array.isArray(block.layout) && block.layout !== layout) {
        processLayout(block.layout);
        if (phone && email && name && company && code) break;
      }
    }
  }

  // Fallback for name
  if (!name) {
    name = item.member?.name || item.name || '';
  }

  // Fallback for email
  if (!email) {
    email = item.member?.email || item.email || '';
  }

  // Fallback for company
  if (!company) {
    company = item.member?.company || item.company || '';
  }

  // Fallback for phone
  if (!phone) {
    const pSource = item.member?.phone || item.phone;
    if (pSource) {
      if (typeof pSource === 'object') {
        const cc = pSource.countryCode
          ? `${String(pSource.countryCode).startsWith('+') ? '' : '+'}${pSource.countryCode} `
          : '';
        const num = pSource.number || '';
        phone = `${cc}${num}`.trim();
      } else {
        phone = String(pSource).trim();
      }
    }
  }

  // Fallback for code
  if (!code && item.localize?.cn?.code) {
    code = item.localize.cn.code;
  }
  if (!code && item.code) {
    code = item.code;
  } else if (!code && item.couponCode) {
    code = item.couponCode;
  } else if (!code && item.ticketCode) {
    code = item.ticketCode;
  }

  const finalCode = code || '-';
  const regTime = item.createdTS ? formatDateTime(item.createdTS) : '-';
  const admissionTime = item.admissionTime
    ? formatDateTime(item.admissionTime)
    : item.isCheckedIn && item.updatedTS
      ? formatDateTime(item.updatedTS)
      : '-';

  return {
    ...item,
    id: item._id,
    name: name || '-',
    phone: phone || '-',
    company: company || '-',
    code: finalCode,
    email: email || '-',
    regTime,
    admissionTime,
    admissionStatus: item.isCheckedIn ? 'admitted' : (item.admissionStatus || 'waiting'),
    localize: {
      ...item.localize,
      cn: {
        ...item.localize?.cn,
        code: finalCode,
      },
    },
  };
};

const EventAdmissionPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const navEvent = location.state?.event;
  const [eventDetail, setEventDetail] = useState(navEvent || null);
  const [eventLoading, setEventLoading] = useState(false);

  const fetchEventDetail = useCallback(async () => {
    const targetId = id || location.state?.event?.eventId || location.state?.event?._id || location.state?.event?.id;
    if (!targetId) return;
    try {
      setEventLoading(true);
      const res = await getDataRequest(`${EVENTS_URL}/${targetId}`);
      setEventLoading(false);
      if (res && (res.status === 200 || res.data)) {
        const data = res.data || res;
        setEventDetail(data);
      }
    } catch (err) {
      setEventLoading(false);
      console.error('Error fetching event detail:', err);
    }
  }, [id, location.state?.event]);

  useEffect(() => {
    fetchEventDetail();
  }, [fetchEventDetail]);

  const effectiveEventId = eventDetail?._id || id || eventDetail?.eventId;

  const [admissionTab, setAdmissionTab] = useState('waiting'); // 'waiting' | 'admitted'
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [rawAttendees, setRawAttendees] = useState([]);
  const [attendees, setAttendees] = useState([]);

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
    sortOrder: undefined,
    sortField: undefined,
  });

  // Detail Modal State
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);

  const eventTitle =
    eventDetail?.localize?.[i18n.language]?.title ||
    eventDetail?.localize?.cn?.title ||
    eventDetail?.localize?.en?.title ||
    eventDetail?.title ||
    '';
  const eventPoster =
    eventDetail?.images?.[0]?.url ||
    eventDetail?.poster ||
    DEFAULT_POSTER;
  const eventTime =
    eventDetail?.startDate && eventDetail?.endDate
      ? `${formatDatePart(eventDetail.startDate)}-${formatDatePart(eventDetail.endDate)}`
      : eventDetail?.time || '';
  const eventCreator =
    eventDetail?.createdBy?.name ||
    eventDetail?.creator ||
    'ESI System';
  const eventStatus = eventDetail?.status;
  const isExpired = Boolean(
    location.state?.tab === 'expired' ||
    location.state?.event?.isExpired ||
    location.state?.event?.tab === 'expired' ||
    eventDetail?.isExpired ||
    eventDetail?.status === 'expired' ||
    (eventDetail?.endDate && new Date(eventDetail.endDate).getTime() < new Date().setHours(0, 0, 0, 0))
  );
  const registeredCount = eventDetail?.registeredCount ?? 0;
  const checkedCount = eventDetail?.checkedCount ?? eventDetail?.checkedCount ?? 0;

  // Fetch Admission List from API
  const fetchAdmission = useCallback(
    async (
      page = tableParams.pagination?.current || 1,
      size = tableParams.pagination?.pageSize || 10,
      kw = searchKeyword,
      sortField = tableParams.sortField,
      sortOrder = tableParams.sortOrder
    ) => {
      if (!effectiveEventId) return;
      try {
        setLoading(true);
        const params = {
          keyword: kw !== undefined ? kw.trim() : '',
          startDate: '',
          endDate: '',
          pageNo: page,
          pageSize: size,
          eventsId: effectiveEventId,
          tab: admissionTab,
        };

        if (sortField && sortOrder) {
          const fieldMap = {
            regTime: 'createdTS',
            admissionTime: 'updatedTS',
          };
          const mappedField = fieldMap[sortField] || sortField;
          params.sortKeys = {
            [mappedField]: sortOrder === 'ascend' ? 'asc' : 'desc',
          };
        }

        const res = await getDataRequest(EVENT_ADMISSION_URL, params);
        setLoading(false);
        if (res && res.status === 200 && Array.isArray(res.data)) {
          const list = res.data.map((item) => normalizeSubmission(item, i18n.language));
          setRawAttendees(res.data);
          setAttendees(list);
          setTableParams((prev) => ({
            ...prev,
            pagination: {
              ...prev.pagination,
              current: page,
              pageSize: size,
              total: typeof res.total === 'number' ? res.total : list.length,
            },
          }));
          if (!eventDetail && res.data[0]?.eventsId && typeof res.data[0].eventsId === 'object') {
            setEventDetail(res.data[0].eventsId);
          }
        } else {
          setRawAttendees([]);
          setAttendees([]);
          setTableParams((prev) => ({
            ...prev,
            pagination: {
              ...prev.pagination,
              total: 0,
            },
          }));
        }
      } catch (err) {
        setLoading(false);
        console.error('Error fetching event admission:', err);
        setRawAttendees([]);
        setAttendees([]);
      }
    },
    [effectiveEventId, admissionTab, searchKeyword, tableParams.pagination?.current, tableParams.pagination?.pageSize, tableParams.sortField, tableParams.sortOrder, eventDetail, i18n.language]
  );

  useEffect(() => {
    fetchAdmission(
      tableParams.pagination?.current || 1,
      tableParams.pagination?.pageSize || 10,
      searchKeyword,
      tableParams.sortField,
      tableParams.sortOrder
    );
  }, [
    admissionTab,
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams?.sortOrder,
    tableParams?.sortField,
    JSON.stringify(tableParams.filters),
    fetchAdmission,
  ]);

  // When language switches, re-normalize attendees in memory without re-calling API or showing loading spinner
  useEffect(() => {
    if (rawAttendees && rawAttendees.length > 0) {
      setAttendees(rawAttendees.map((item) => normalizeSubmission(item, i18n.language)));
    }
  }, [i18n.language]);

  // Handle Fetch Details for Selected Attendee
  const handleOpenDetail = async (item) => {
    const detailId = item._id || item.id;
    if (!detailId) return;
    try {
      setSelectedAttendee(item);
      setDetailModalVisible(true);
      setDetailLoading(true);
      const res = await getDataRequest(`${EVENT_SUBMISSIONS_URL}/${detailId}`);
      setDetailLoading(false);
      if (res && (res.status === 200 || res.data)) {
        const detailItem = res.data || res;
        const normalized = normalizeSubmission(detailItem, i18n.language);
        setSelectedAttendee(normalized);
      }
    } catch (err) {
      setDetailLoading(false);
      console.error('Error fetching submission detail:', err);
    }
  };

  const applyStylesToSheet = (worksheet, headerRowIndices, boldCellKeys = []) => {
    if (!worksheet['!ref']) return;
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const borderStyle = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    };

    const rowHeights = [];
    for (let R = range.s.r; R <= range.e.r; ++R) {
      rowHeights[R] = { hpt: 22 };
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;

        const isHeaderRow = headerRowIndices.includes(R);
        const isBoldCell = boldCellKeys.includes(cellAddress);

        worksheet[cellAddress].s = {
          ...worksheet[cellAddress].s,
          border: borderStyle,
          font: isHeaderRow || isBoldCell ? { bold: true } : {},
          alignment: { vertical: 'center', horizontal: 'left' },
        };
      }
    }
    worksheet['!rows'] = rowHeights;
  };

  const autoSizeColumns = (worksheet) => {
    if (!worksheet['!ref']) return;
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const colWidths = [];

    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxWidth = 10;
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cellAddress];
        if (cell && cell.v !== undefined && cell.v !== null) {
          const cellValue = String(cell.v);
          let length = 0;
          for (let i = 0; i < cellValue.length; i++) {
            if (cellValue.charCodeAt(i) > 255) {
              length += 2.2;
            } else {
              length += 1.2;
            }
          }
          if (length > maxWidth) {
            maxWidth = length;
          }
        }
      }
      colWidths[C] = { wch: Math.ceil(maxWidth) + 2 };
    }
    worksheet['!cols'] = colWidths;
  };

  const handleExport = async () => {
    try {
      // message.loading({ content: t('Preparing Excel export...'), key: 'exporting' });
      const params = {
        keyword: searchKeyword ? searchKeyword.trim() : '',
        startDate: '',
        endDate: '',
        pageNo: 1,
        pageSize: 10000,
        eventsId: effectiveEventId || '',
        tab: admissionTab,
      };
      const res = await getDataRequest(EVENT_ADMISSION_URL, params);
      message.destroy('exporting');
      const list = (res && (Array.isArray(res.data) ? res.data : res.data?.list)) || [];
      const normalizedList = list.map((item) => normalizeSubmission(item, i18n.language));

      const isAdmitted = admissionTab === 'admitted';
      const aoa = [
        isAdmitted
          ? [
            t('Applicant Name'),
            t('Phone Number'),
            t('Salon / Company'),
            t('Invitation Code'),
            t('Email'),
            t('Registration Time'),
            t('Admission Time'),
            t('Status'),
          ]
          : [
            t('Applicant Name'),
            t('Phone Number'),
            t('Salon / Company'),
            t('Invitation Code'),
            t('Email'),
            t('Registration Time'),
            t('Status'),
          ],
      ];

      normalizedList.forEach((app) => {
        if (isAdmitted) {
          aoa.push([
            app.name || '-',
            app.phone || '-',
            app.company || '-',
            app.code || '-',
            app.email || '-',
            app.regTime || '-',
            app.admissionTime || '-',
            t('Admitted'),
          ]);
        } else {
          aoa.push([
            app.name || '-',
            app.phone || '-',
            app.company || '-',
            app.code || '-',
            app.email || '-',
            app.regTime || '-',
            t('Waiting for Admission'),
          ]);
        }
      });

      const worksheet = XLSX.utils.aoa_to_sheet(aoa);
      applyStylesToSheet(worksheet, [0]);
      autoSizeColumns(worksheet);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      const filename = isAdmitted ? '已入场名单' : '等待入场名单';
      XLSX.writeFile(workbook, `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      // message.success(t('Exported successfully!'));
    } catch (err) {
      message.destroy('exporting');
      console.error('Export error:', err);
      message.error(t('Export failed'));
    }
  };

  const tabItems = [
    { key: 'waiting', label: t('Waiting for Admission') },
    { key: 'admitted', label: t('Admitted')},
  ];

  // Standard Table Columns matching Screenshots
  const columns =
    admissionTab === 'admitted'
      ? [
        {
          title: <label className="titleCard14400">{t('Registration Info')}</label>,
          key: 'info',
          width: '40%',
          render: (_, item) => (
            <div style={{ lineHeight: '1.7', fontSize: 13 }}>
              <div>
                <span style={{ color: '#636E88' }}>{t('Applicant Name')}：</span>
                <span style={{ color: '#262626', fontWeight: 500 }}>{item.name}</span>
              </div>
              <div>
                <span style={{ color: '#636E88' }}>{t('Phone Number') || t('Mobile Phone')}：</span>
                <span style={{ color: '#262626' }}>{item.phone}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div>
                  <span style={{ color: '#636E88' }}>{t('Salon / Company')}：</span>
                  <span style={{ color: '#262626' }}>{item.company}</span>
                </div>
                <span
                  style={{ color: '#0075ff', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onClick={() => handleOpenDetail(item)}
                >
                  {t('View Details') || t('View Detail Info')}
                </span>
              </div>
            </div>
          ),
        },
        {
          title: (
            <label className="ant-table-column-title titleCard14400">
              {t('Registration Time')}
            </label>
          ),
          dataIndex: 'regTime',
          key: 'regTime',
          sorter: true,
          sortOrder: tableParams.sortField === 'regTime' ? tableParams.sortOrder : null,
          width: '30%',
          render: (text) => <span style={{ color: '#262626', fontSize: 13 }}>{text}</span>,
        },
        {
          title: (
            <label className="ant-table-column-title titleCard14400">
              {t('Admission Time')}
            </label>
          ),
          dataIndex: 'admissionTime',
          key: 'admissionTime',
          sorter: true,
          sortOrder: tableParams.sortField === 'admissionTime' ? tableParams.sortOrder : null,
          width: '30%',
          render: (text) => (
            <span style={{ color: '#262626', fontSize: 13 }}>
              {text || '-'}
            </span>
          ),
        },
      ]
      : [
        {
          title: <label className="titleCard14400">{t('Registration Info')}</label>,
          key: 'info',
          width: '45%',
          render: (_, item) => (
            <div style={{ lineHeight: '1.7', fontSize: 13 }}>
              <div>
                <span style={{ color: '#636E88' }}>{t('Applicant Name')}：</span>
                <span style={{ color: '#262626', fontWeight: 500 }}>{item.name}</span>
              </div>
              <div>
                <span style={{ color: '#636E88' }}>{t('Phone Number') || t('Mobile Phone')}：</span>
                <span style={{ color: '#262626' }}>{item.phone}</span>
              </div>
              <div>
                <span style={{ color: '#636E88' }}>{t('Salon / Company')}：</span>
                <span style={{ color: '#262626' }}>{item.company}</span>
              </div>
            </div>
          ),
        },
        {
          title: (
            <label className="ant-table-column-title titleCard14400">
              {t('Registration Time')}
            </label>
          ),
          dataIndex: 'regTime',
          key: 'regTime',
          sorter: true,
          sortOrder: tableParams.sortField === 'regTime' ? tableParams.sortOrder : null,
          width: '35%',
          render: (text) => <span style={{ color: '#262626', fontSize: 13 }}>{text}</span>,
        },
        {
          title: <label className="titleCard14400">{t('Action')}</label>,
          key: 'action',
          width: '20%',
          render: (_, item) => (
            <span
              style={{ color: '#0075ff', fontSize: 13, cursor: 'pointer' }}
              onClick={() => handleOpenDetail(item)}
            >
              {t('View Details') || t('View Detail Info')}
            </span>
          ),
        },
      ];

  // Client-side sort fallback if sortField / sortOrder is set
  const displayedAttendees = useMemo(() => {
    if (!tableParams.sortField || !tableParams.sortOrder) return attendees;
    return [...attendees].sort((a, b) => {
      const fieldA = String(a[tableParams.sortField] || '');
      const fieldB = String(b[tableParams.sortField] || '');
      if (tableParams.sortOrder === 'ascend') {
        return fieldA.localeCompare(fieldB);
      } else if (tableParams.sortOrder === 'descend') {
        return fieldB.localeCompare(fieldA);
      }
      return 0;
    });
  }, [attendees, tableParams.sortField, tableParams.sortOrder]);

  return (
    <HeaderComponent
      isHide="view"
      pageTitle={
        <span
          style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
          onClick={() => navigate('/events')}
        >
          <ArrowLeftOutlined /> {t('Event Admission')}
        </span>
      }
    >
      <div className="box-shadow h-100 w-100 event-page-wrapper">
        <div className="w-100 h-100 overflow-auto">
          <div className="event-subpage-container">
            {/* 2-Column Layout */}
            <div className="event-subpage-layout">
              {/* Left Column: Compact Event Summary Card */}
              <div className="event-summary-col">
                <Spin spinning={eventLoading}>
                  <div className="event-summary-card">
                    <div className="event-summary-header">
                      <img
                        src={eventPoster}
                        alt={eventTitle}
                        className="thumb"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = DEFAULT_POSTER;
                        }}
                      />
                      <h3 className="title">{eventTitle}</h3>
                    </div>

                    <div className="event-summary-meta">
                      <div style={{ color: '#1f2937' }}>{t('Event Time')}: {eventTime}</div>
                      <div>{t('Creator')}: {eventCreator}</div>
                    </div>

                    <div className="event-summary-bottom">
                      <span
                        className={isExpired ? 'tag-expired' : 'tag-active'}
                        style={{
                          borderRadius: 12,
                          padding: '2px 10px',
                          backgroundColor: isExpired ? '#F3F4F6' : undefined,
                          color: isExpired ? '#6B7280' : undefined,
                        }}
                      >
                        {isExpired ? (t('Expired')) : (t('Ongoing'))}
                      </span>
                      <div className="stat-red">
                        <span style={{ color: '#E50A47' }}>{t('Admitted Count')}: </span>
                        <span style={{ fontWeight: 600, color: '#15803d' }}>{checkedCount}</span><span style={{ fontWeight: 600, color: '#636E88' }}>/{registeredCount}</span>
                      </div>
                    </div>
                  </div>
                </Spin>
              </div>

              {/* Right Column: Admission List */}
              <div className="event-main-content-col">
                {/* Tabs with System .cusStyle and Right Scan Button */}
                <Tabs
                  activeKey={admissionTab}
                  onChange={(key) => {
                    setAdmissionTab(key);
                    setTableParams((prev) => ({
                      ...prev,
                      pagination: {
                        ...prev.pagination,
                        current: 1,
                      },
                      sortOrder: undefined,
                      sortField: undefined,
                    }));
                  }}
                  className="cusStyle"
                  items={tabItems}
                  tabBarExtraContent={
                    <>
                      {
                        !isExpired ? (
                          <Button
                            style={{
                              fontSize: 14,
                              backgroundColor: '#FCE7EC',
                              border: 'none',
                              color: '#E50A47',
                              fontWeight: 500,
                              borderRadius: 4,
                              height: 32,
                              padding: '0 18px',
                              cursor: 'pointer',
                            }}
                            onClick={() => navigate(`/events/${effectiveEventId}/scan`)}
                          >
                            {t('Scan')}
                          </Button>) : null
                      }
                    </>
                  }
                />

                {/* Search Filter Bar */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                  }}
                >
                  <Input
                    prefix={<SearchSvg />}
                    placeholder={t('Search')}
                    value={searchKeyword}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSearchKeyword(val);
                      if (!val) {
                        setTableParams((prev) => ({
                          ...prev,
                          pagination: { ...prev.pagination, current: 1 },
                        }));
                        fetchAdmission(1, tableParams.pagination?.pageSize || 10, '', tableParams.sortField, tableParams.sortOrder);
                      }
                    }}
                    onPressEnter={() => {
                      setTableParams((prev) => ({
                        ...prev,
                        pagination: { ...prev.pagination, current: 1 },
                      }));
                      fetchAdmission(1, tableParams.pagination?.pageSize || 10, searchKeyword, tableParams.sortField, tableParams.sortOrder);
                    }}
                    style={{ borderRadius: 4, height: 32, borderColor: '#d9d9d9', width: 320 }}
                    allowClear
                  />

                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <Button
                      style={{
                        height: 32,
                        padding: '0 18px',
                        borderRadius: 4,
                        borderColor: '#D9D9D9',
                        color: '#262626',
                        background: '#FFFFFF',
                      }}
                      onClick={() => {
                        setTableParams((prev) => ({
                          ...prev,
                          pagination: {
                            ...prev.pagination,
                            current: 1,
                          },
                        }));
                        fetchAdmission(1, tableParams.pagination?.pageSize || 10, searchKeyword, tableParams.sortField, tableParams.sortOrder);
                      }}
                    >
                      {t('Query')}
                    </Button>

                    <div
                      onClick={handleExport}
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: 4,
                        borderRadius: 4,
                      }}
                      title={t('Export')}
                    >
                      <ExportExcelSvg />
                    </div>
                  </div>
                </div>

                {/* Standard System Table with Ant Design & TableFetchData */}
                <div className="w-100">
                  <TableFetchData
                    tableParams={tableParams}
                    setTableParams={setTableParams}
                    data={displayedAttendees}
                    columns={columns}
                    loading={loading}
                    setLoading={setLoading}
                    rowKey={(r) => r._id || r.id}
                    x="100%"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Attendee Details Modal */}
        <Modal
          title={admissionTab === "waiting" ? t('Waiting for Admission Details') :t('Attendee Details')}
          open={detailModalVisible}
          onCancel={() => {
            setDetailModalVisible(false);
            setSelectedAttendee(null);
          }}
          footer={false}
        >
          <Spin spinning={detailLoading}>
            {selectedAttendee && (
              <div style={{ padding: '8px 0' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                    paddingBottom: 12,
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <div>
                    <span style={{ fontSize: 18, fontWeight: 600, color: '#262626' }}>
                      {selectedAttendee.name}
                    </span>
                    <span style={{ marginLeft: 10, color: '#636E88' }}>
                      {selectedAttendee.phone}
                    </span>
                  </div>
                  <Tag
                    color={
                      selectedAttendee.admissionStatus === 'admitted' || selectedAttendee.isCheckedIn
                        ? 'green'
                        : 'orange'
                    }
                  >
                    {selectedAttendee.admissionStatus === 'admitted' || selectedAttendee.isCheckedIn
                      ? (t('Admitted'))
                      : (t('Waiting for Admission'))}
                  </Tag>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
                  <div>
                    <div style={{ color: '#636E88' }}>{t('Salon / Company')}</div>
                    <div style={{ fontWeight: 500, color: '#262626', marginTop: 2 }}>
                      {selectedAttendee.company}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#636E88' }}>{t('Invitation Code')}</div>
                    <div style={{ fontWeight: 600, color: '#E50A47', marginTop: 2 }}>
                      {selectedAttendee.code || selectedAttendee.ticketCode}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#636E88' }}>{t('Email')}</div>
                    <div style={{ fontWeight: 500, color: '#262626', marginTop: 2 }}>
                      {selectedAttendee.email || (t('Not filled'))}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#636E88' }}>{t('Registration Time')}</div>
                    <div style={{ fontWeight: 500, color: '#262626', marginTop: 2 }}>
                      {selectedAttendee.regTime}
                    </div>
                  </div>
                  {/* {(selectedAttendee.admissionTime || selectedAttendee.isCheckedIn) && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ color: '#636E88' }}>{t('Actual Admission Verification Time')}</div>
                      <div style={{ fontWeight: 600, color: '#16a34a', marginTop: 2 }}>
                        {selectedAttendee.admissionTime || formatDateTime(selectedAttendee.updatedTS)}
                      </div>
                    </div>
                  )} */}
                </div>

                {/* Virtual Ticket Pass Box */}
                <div
                  style={{
                    marginTop: 20,
                    background: '#fafbfc',
                    border: '1px dashed #cbd5e1',
                    borderRadius: 8,
                    padding: 16,
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 12, color: '#636E88', marginBottom: 8 }}>
                    {t('Admission Voucher')}
                  </div>
                  <div
                    style={{
                      display: 'inline-block',
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      padding: 8,
                      borderRadius: 6,
                    }}
                  >
                    <QRCode
                      value={selectedAttendee?.ticketCode}
                      size={200}
                      icon={logo}
                      iconSize={36}
                      bordered={false}
                      errorLevel="H"
                    />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 2, color: '#262626', marginTop: 6 }}>
                    {selectedAttendee?.ticketCode}
                  </div>
                </div>
              </div>
            )}
          </Spin>
        </Modal>
      </div>
    </HeaderComponent>
  );
};

export default EventAdmissionPage;
